import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import {
  Plus, X, Trash2, Truck, Receipt, ArrowLeft, Loader2, CreditCard,
  Ban, CheckCircle2, Mail, Phone, Search, ChevronRight, Edit3, FileCheck, FileDown, Send,
} from "lucide-react";
import StatementModal from "@/components/bo/StatementModal";
import Report1099Panel from "@/components/bo/Report1099Panel";
import BulkSendModal from "@/components/bo/BulkSendModal";

const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS_PILLS = {
  unpaid:    { en: "Unpaid",    es: "Por Pagar",  cls: "bg-white border-[#C4A252] text-[#8B1C22]" },
  partial:   { en: "Partial",   es: "Parcial",    cls: "bg-[#FDFBF7] border-[#C4A252] text-[#C4A252]" },
  paid:      { en: "Paid",      es: "Pagada",     cls: "bg-white border-[#15803D] text-[#15803D]" },
  overdue:   { en: "Overdue",   es: "Vencida",    cls: "bg-white border-[#8B1C22] text-[#8B1C22]" },
  cancelled: { en: "Cancelled", es: "Cancelada",  cls: "bg-white border-[#737373] text-[#737373] line-through" },
};

const TERMS = [
  { key: "due_on_receipt", en: "Due on Receipt", es: "Pago Inmediato" },
  { key: "net_7", en: "Net 7", es: "Neto 7" },
  { key: "net_15", en: "Net 15", es: "Neto 15" },
  { key: "net_30", en: "Net 30", es: "Neto 30" },
  { key: "net_60", en: "Net 60", es: "Neto 60" },
];

const Modal = ({ open, onClose, title, children, testid, wide = false }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={testid}>
      <div className={`bg-white ${wide ? "max-w-3xl" : "max-w-lg"} w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl">{title}</h3>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

/* Vendor form */
const VendorForm = ({ bookId, lang, existing, onCancel, onSaved }) => {
  const [form, setForm] = useState(existing || {
    name: "", company: "", email: "", phone: "", address: "",
    tax_id: "", is_1099: false, default_terms: "net_30", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (existing) {
        const { data } = await api.patch(`/back-office/books/${bookId}/vendors/${existing.id}`, form);
        toast.success("Vendor updated"); onSaved(data);
      } else {
        const { data } = await api.post(`/back-office/books/${bookId}/vendors`, form);
        toast.success("Vendor added"); onSaved(data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="vendor-form">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Nombre" : "Name"}</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rn3-input" data-testid="vf-name" /></div>
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Empresa" : "Company"}</label><input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rn3-input" data-testid="vf-company" /></div>
        <div><label className="rn3-eyebrow block mb-2">Email</label><input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rn3-input" data-testid="vf-email" /></div>
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Teléfono" : "Phone"}</label><input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rn3-input" data-testid="vf-phone" /></div>
      </div>
      <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Dirección" : "Address"}</label><textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rn3-input" rows="2" data-testid="vf-addr" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="rn3-eyebrow block mb-2">EIN / SSN</label><input value={form.tax_id || ""} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="rn3-input" placeholder="XX-XXXXXXX" data-testid="vf-tax" /></div>
        <div>
          <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Términos" : "Default Terms"}</label>
          <select value={form.default_terms} onChange={(e) => setForm({ ...form, default_terms: e.target.value })} className="rn3-input" data-testid="vf-terms">
            {TERMS.map((t) => <option key={t.key} value={t.key}>{lang === "es" ? t.es : t.en}</option>)}
          </select>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!form.is_1099} onChange={(e) => setForm({ ...form, is_1099: e.target.checked })} data-testid="vf-1099" />
        <FileCheck className="w-4 h-4 text-[#C4A252]" />
        {lang === "es" ? "Este proveedor requiere 1099" : "This vendor is 1099-eligible (track for year-end)"}
      </label>
      <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Notas" : "Notes"}</label><textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rn3-input" rows="2" /></div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="rn3-btn-gold">Cancel</button>
        <button disabled={busy} className="rn3-btn-primary" data-testid="vf-submit">{busy ? "…" : (existing ? "Save" : "Add Vendor")}</button>
      </div>
    </form>
  );
};

/* Bill form */
const BillForm = ({ bookId, vendors, expenseAccounts, lang, existing, onCancel, onSaved }) => {
  const [form, setForm] = useState(existing || {
    vendor_id: vendors[0]?.id || "",
    bill_number: "",
    date: todayISO(),
    terms: "net_30",
    items: [{ description: "", quantity: 1, unit_price: 0, account_code: expenseAccounts[0]?.code || "6900" }],
    tax_rate: 0,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const totals = useMemo(() => {
    const sub = (form.items || []).reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.unit_price || 0)), 0);
    const tax = sub * Number(form.tax_rate || 0) / 100;
    return { subtotal: sub, tax, total: sub + tax };
  }, [form]);
  const upd = (i, k, v) => { const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items }); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (existing) {
        const { data } = await api.patch(`/back-office/books/${bookId}/bills/${existing.id}`, {
          vendor_id: form.vendor_id, bill_number: form.bill_number, date: form.date, terms: form.terms,
          items: form.items, tax_rate: Number(form.tax_rate || 0), notes: form.notes,
        });
        toast.success("Bill updated"); onSaved(data);
      } else {
        const { data } = await api.post(`/back-office/books/${bookId}/bills`, { ...form, tax_rate: Number(form.tax_rate || 0) });
        toast.success(`Created ${data.internal_number}`); onSaved(data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="bill-form">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Proveedor" : "Vendor"}</label>
          <select required value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} className="rn3-input" data-testid="bf-vendor">
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}{v.company ? ` · ${v.company}` : ""}</option>)}
          </select>
        </div>
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Términos" : "Terms"}</label>
          <select value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="rn3-input" data-testid="bf-terms">
            {TERMS.map((t) => <option key={t.key} value={t.key}>{lang === "es" ? t.es : t.en}</option>)}
          </select>
        </div>
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Fecha" : "Date"}</label><input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rn3-input" data-testid="bf-date" /></div>
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Número del Proveedor" : "Vendor's Bill #"}</label><input value={form.bill_number || ""} onChange={(e) => setForm({ ...form, bill_number: e.target.value })} className="rn3-input" data-testid="bf-num" /></div>
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Tasa de Impuesto (%)" : "Tax Rate (%)"}</label><input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className="rn3-input" data-testid="bf-tax" /></div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="rn3-eyebrow">{lang === "es" ? "Líneas" : "Line Items"}</div>
          <button type="button" onClick={() => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0, account_code: expenseAccounts[0]?.code || "6900" }] })} className="text-xs text-[#8B1C22] inline-flex items-center gap-1" data-testid="bf-add-item"><Plus className="w-3 h-3" /> {lang === "es" ? "Línea" : "Add Line"}</button>
        </div>
        <div className="space-y-2">
          {form.items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`bf-item-${i}`}>
              <input value={it.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder={lang === "es" ? "Descripción" : "Description"} className="col-span-5 rn3-input text-sm" required />
              <input type="number" step="0.01" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} placeholder="Qty" className="col-span-1 rn3-input text-sm" />
              <input type="number" step="0.01" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} placeholder="Price" className="col-span-2 rn3-input text-sm" />
              <select value={it.account_code} onChange={(e) => upd(i, "account_code", e.target.value)} className="col-span-3 rn3-input text-sm">
                {expenseAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} {(lang === "es" && a.name_es) || a.name}</option>)}
              </select>
              <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })} className="col-span-1 text-[#737373] hover:text-[#8B1C22]"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-[#E5E5E5] pt-4 grid grid-cols-2 gap-4">
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Notas" : "Notes"}</label><textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rn3-input" rows="2" /></div>
        <div className="text-right space-y-1 text-sm">
          <div className="flex justify-between"><span>{lang === "es" ? "Subtotal" : "Subtotal"}</span><span className="tabular-nums">{fmt(totals.subtotal)}</span></div>
          <div className="flex justify-between text-[#737373]"><span>{lang === "es" ? "Impuesto" : "Tax"} ({form.tax_rate}%)</span><span className="tabular-nums">{fmt(totals.tax)}</span></div>
          <div className="flex justify-between font-serif-display text-2xl pt-2 border-t border-[#C4A252]"><span>{lang === "es" ? "Total" : "Total"}</span><span className="tabular-nums">{fmt(totals.total)}</span></div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="rn3-btn-gold">Cancel</button>
        <button disabled={busy} className="rn3-btn-primary" data-testid="bf-submit">{busy ? "…" : (existing ? (lang === "es" ? "Guardar" : "Save Changes") : (lang === "es" ? "Crear Factura" : "Create Bill"))}</button>
      </div>
    </form>
  );
};

/* Bill Payment form */
const BillPayForm = ({ bookId, bill, accounts, lang, onCancel, onSaved }) => {
  const bankAccounts = accounts.filter((a) => !a.is_header && a.is_active && (a.subtype === "bank" || a.code.startsWith("10")));
  const [form, setForm] = useState({
    date: todayISO(),
    amount: bill.balance || bill.total,
    cash_account_code: bankAccounts[0]?.code || "1010",
    method: "check",
    reference: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/back-office/books/${bookId}/bills/${bill.id}/payments`, { ...form, amount: Number(form.amount) });
      toast.success(lang === "es" ? "Pago registrado" : "Payment recorded");
      onSaved(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="bill-pay-form">
      <div className="bg-[#FDFBF7] border border-[#C4A252] p-3 text-sm">
        <div>{bill.internal_number} · {bill.vendor_name}</div>
        <div className="font-serif-display text-2xl mt-1">{lang === "es" ? "Saldo" : "Balance"}: {fmt(bill.balance)}</div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Fecha" : "Date"}</label><input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rn3-input" data-testid="bp-date" /></div>
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Monto" : "Amount"}</label><input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rn3-input" data-testid="bp-amount" /></div>
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Pagar Desde" : "Pay From"}</label>
          <select value={form.cash_account_code} onChange={(e) => setForm({ ...form, cash_account_code: e.target.value })} className="rn3-input" data-testid="bp-bank">
            {bankAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
        </div>
        <div><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Método" : "Method"}</label>
          <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="rn3-input" data-testid="bp-method">
            {["check","cash","ach","card","wire","other"].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
          </select>
        </div>
        <div className="col-span-2"><label className="rn3-eyebrow block mb-2">{lang === "es" ? "Referencia" : "Reference / Check #"}</label><input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="rn3-input" data-testid="bp-ref" /></div>
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="rn3-btn-gold">Cancel</button>
        <button disabled={busy} className="rn3-btn-primary" data-testid="bp-submit">{busy ? "…" : (lang === "es" ? "Registrar Pago" : "Record Payment")}</button>
      </div>
    </form>
  );
};

/* Bill detail */
const BillDetail = ({ bookId, billId, accounts, lang, onBack, onChanged }) => {
  const [bill, setBill] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPay, setShowPay] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const expenseAccounts = useMemo(() => accounts.filter((a) => ["expense", "cogs"].includes(a.type) && !a.is_header && a.is_active), [accounts]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: vs }] = await Promise.all([
        api.get(`/back-office/books/${bookId}/bills/${billId}`),
        api.get(`/back-office/books/${bookId}/vendors`),
      ]);
      setBill(b); setVendors(vs);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [billId]);

  const setStatus = async (status) => {
    try {
      const { data } = await api.patch(`/back-office/books/${bookId}/bills/${billId}`, { status });
      setBill((p) => ({ ...p, ...data }));
      onChanged?.();
    } catch { toast.error("Failed"); }
  };
  const removeBill = async () => {
    if (!window.confirm("Delete this bill?")) return;
    try { await api.delete(`/back-office/books/${bookId}/bills/${billId}`); toast.success("Deleted"); onBack(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const voidPayment = async (paymentId) => {
    if (!window.confirm("Void this payment?")) return;
    await api.delete(`/back-office/books/${bookId}/bills/${billId}/payments/${paymentId}`);
    toast.success("Payment voided"); load(); onChanged?.();
  };

  if (loading || !bill) return <div className="py-12 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;
  const v = bill.vendor || {};
  const accName = (code) => accounts.find((a) => a.code === code);
  const pill = STATUS_PILLS[bill.status] || STATUS_PILLS.unpaid;

  return (
    <div className="space-y-6" data-testid="bill-detail">
      <button onClick={onBack} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid="bill-back">
        <ArrowLeft className="w-3 h-3" /> {lang === "es" ? "Volver" : "Back to Bills"}
      </button>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 rn3-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="rn3-eyebrow flex items-center gap-2">
                {bill.internal_number}
                {bill.bill_number && <span className="text-[#737373]">· {lang === "es" ? "Ref" : "Ref"}: {bill.bill_number}</span>}
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${pill.cls}`}>{lang === "es" ? pill.es : pill.en}</span>
              </div>
              <h2 className="font-serif-display text-3xl mt-2">{v.name}</h2>
              {v.company && <div className="text-sm text-[#737373]">{v.company}</div>}
              {v.is_1099 && <div className="text-xs text-[#C4A252] mt-1 inline-flex items-center gap-1"><FileCheck className="w-3 h-3" /> {lang === "es" ? "Requiere 1099" : "1099-eligible"}</div>}
              <div className="text-xs text-[#737373] mt-1 space-y-0.5">
                {v.email && <div className="inline-flex items-center gap-1 mr-3"><Mail className="w-3 h-3" /> {v.email}</div>}
                {v.phone && <div className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {v.phone}</div>}
              </div>
            </div>
            <div className="text-right text-sm">
              <div>{lang === "es" ? "Fecha" : "Date"}: {bill.date}</div>
              <div>{lang === "es" ? "Vence" : "Due"}: <b>{bill.due_date}</b></div>
              <div className="text-xs text-[#737373] mt-1">{TERMS.find(t=>t.key===bill.terms)?.[lang]||bill.terms}</div>
            </div>
          </div>
          <table className="w-full text-sm mt-6">
            <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5]">
              <tr><th className="py-2">{lang === "es" ? "Descripción" : "Description"}</th><th className="py-2 text-right w-16">Qty</th><th className="py-2 text-right w-24">{lang === "es" ? "Precio" : "Price"}</th><th className="py-2 text-right w-24">{lang === "es" ? "Total" : "Total"}</th></tr>
            </thead>
            <tbody>
              {(bill.items || []).map((it, i) => (
                <tr key={i} className="border-b border-[#F3EFE9]">
                  <td className="py-2"><div>{it.description}</div><div className="text-xs text-[#737373]">{accName(it.account_code)?.code} — {(lang === "es" && accName(it.account_code)?.name_es) || accName(it.account_code)?.name}</div></td>
                  <td className="py-2 text-right tabular-nums">{it.quantity}</td>
                  <td className="py-2 text-right tabular-nums">{fmt(it.unit_price)}</td>
                  <td className="py-2 text-right tabular-nums">{fmt(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-6 text-sm space-y-1 max-w-xs ml-auto">
            <div className="flex justify-between"><span>{lang === "es" ? "Subtotal" : "Subtotal"}</span><span className="tabular-nums">{fmt(bill.subtotal)}</span></div>
            <div className="flex justify-between text-[#737373]"><span>{lang === "es" ? "Impuesto" : "Tax"} ({bill.tax_rate}%)</span><span className="tabular-nums">{fmt(bill.tax_amount)}</span></div>
            <div className="flex justify-between font-serif-display text-2xl border-t border-[#C4A252] pt-2"><span>{lang === "es" ? "Total" : "Total"}</span><span className="tabular-nums">{fmt(bill.total)}</span></div>
            <div className="flex justify-between text-[#15803D]"><span>{lang === "es" ? "Pagado" : "Paid"}</span><span className="tabular-nums">{fmt(bill.amount_paid)}</span></div>
            <div className={`flex justify-between font-serif-display text-xl ${bill.balance > 0 ? "text-[#8B1C22]" : "text-[#15803D]"}`}><span>{lang === "es" ? "Saldo" : "Balance"}</span><span className="tabular-nums">{fmt(bill.balance)}</span></div>
          </div>
          {bill.notes && <div className="mt-6 border-t border-[#E5E5E5] pt-4 text-sm text-[#404040]"><b>{lang === "es" ? "Notas" : "Notes"}:</b> {bill.notes}</div>}
        </div>
        <aside className="space-y-3">
          {bill.status !== "cancelled" && bill.balance > 0.005 && (
            <button onClick={() => setShowPay(true)} className="rn3-btn-primary w-full justify-center" data-testid="pay-bill-btn"><CreditCard className="w-4 h-4" /> {lang === "es" ? "Pagar Factura" : "Pay Bill"}</button>
          )}
          {!["paid", "cancelled"].includes(bill.status) && bill.amount_paid === 0 && (
            <button onClick={() => setShowEdit(true)} className="rn3-btn-gold w-full justify-center" data-testid="edit-bill-btn"><Edit3 className="w-4 h-4" /> {lang === "es" ? "Editar" : "Edit Bill"}</button>
          )}
          {bill.status !== "cancelled" && bill.status !== "paid" && (
            <button onClick={() => setStatus("cancelled")} className="text-xs text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 w-full justify-center py-2"><Ban className="w-3 h-3" /> {lang === "es" ? "Cancelar" : "Cancel Bill"}</button>
          )}
          {bill.amount_paid === 0 && (
            <button onClick={removeBill} className="text-xs text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 w-full justify-center py-2"><Trash2 className="w-3 h-3" /> {lang === "es" ? "Eliminar" : "Delete"}</button>
          )}
          {(bill.payments || []).length > 0 && (
            <div className="border border-[#E5E5E5] p-4">
              <div className="rn3-eyebrow mb-3">{lang === "es" ? "Pagos" : "Payments"}</div>
              <div className="space-y-2">
                {bill.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border-b border-[#F3EFE9] pb-2">
                    <div>
                      <div className="text-[#0A0A0A]">{fmt(p.amount)} <span className="text-xs text-[#737373] uppercase ml-2">{p.method}</span></div>
                      <div className="text-xs text-[#737373]">{p.date}{p.reference ? ` · ${p.reference}` : ""}</div>
                    </div>
                    <button onClick={() => voidPayment(p.id)} className="text-[#737373] hover:text-[#8B1C22]" title="Void"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
      <Modal open={showPay} onClose={() => setShowPay(false)} title={lang === "es" ? "Pagar Factura" : "Pay Bill"} testid="bill-pay-modal">
        <BillPayForm bookId={bookId} bill={{ ...bill, vendor_name: v.name }} accounts={accounts} lang={lang} onCancel={() => setShowPay(false)} onSaved={() => { setShowPay(false); load(); onChanged?.(); }} />
      </Modal>
      <Modal open={showEdit} onClose={() => setShowEdit(false)} title={lang === "es" ? "Editar Factura" : "Edit Bill"} testid="edit-bill-modal" wide>
        <BillForm bookId={bookId} vendors={vendors} expenseAccounts={expenseAccounts} lang={lang} existing={bill} onCancel={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); onChanged?.(); }} />
      </Modal>
    </div>
  );
};

/* A/P Aging panel */
const APAgingPanel = ({ bookId, lang, onVendorClick }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/back-office/books/${bookId}/reports/ap-aging`).then(({ data }) => setData(data)).catch(() => {}); }, [bookId]);
  if (!data) return null;
  return (
    <div className="rn3-card" data-testid="ap-aging-panel">
      <div className="rn3-eyebrow">{lang === "es" ? "Antigüedad de Cuentas por Pagar" : "A/P Aging"}</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
        {[["current","Current","Al día"],["1_30","1–30","1–30"],["31_60","31–60","31–60"],["61_90","61–90","61–90"],["over_90","90+","90+"]].map(([k, e, es]) => (
          <div key={k} className="border border-[#E5E5E5] p-3">
            <div className={`text-xs uppercase tracking-widest ${k === "over_90" ? "text-[#8B1C22]" : k === "61_90" ? "text-[#C4A252]" : "text-[#737373]"}`}>{lang === "es" ? es : e}</div>
            <div className="font-serif-display text-2xl mt-1 tabular-nums">{fmt(data.buckets[k])}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 text-right text-sm">{lang === "es" ? "Total Por Pagar" : "Total Payable"}: <b className="text-[#0A0A0A]">{fmt(data.total)}</b></div>
      {data.vendors.length > 0 && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-widest text-[#737373] mb-2">{lang === "es" ? "Por Proveedor" : "By Vendor"}</div>
          <div className="space-y-1.5">
            {data.vendors.map((v) => (
              <button key={v.vendor_id} onClick={() => onVendorClick(v.vendor_id)} className="w-full flex items-center justify-between text-sm border border-[#E5E5E5] px-3 py-2 hover:bg-[#FDFBF7]">
                <span>{v.vendor_name}{v.vendor_company ? ` · ${v.vendor_company}` : ""}</span>
                <span className="font-serif-display text-lg tabular-nums">{fmt(v.total)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* Main BillsTab */
const BillsTab = ({ book, accounts, lang }) => {
  const [view, setView] = useState("bills");
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewBill, setShowNewBill] = useState(false);
  const [showNewVend, setShowNewVend] = useState(false);
  const [editVend, setEditVend] = useState(null);
  const [stmtVend, setStmtVend] = useState(null);
  const [bulkVendorStmts, setBulkVendorStmts] = useState(false);
  const [activeBill, setActiveBill] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterVendor, setFilterVendor] = useState("");
  const [q, setQ] = useState("");
  const expenseAccounts = useMemo(() => accounts.filter((a) => ["expense", "cogs"].includes(a.type) && !a.is_header && a.is_active), [accounts]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: bs }, { data: vs }] = await Promise.all([
        api.get(`/back-office/books/${book.id}/bills`),
        api.get(`/back-office/books/${book.id}/vendors`),
      ]);
      setBills(bs); setVendors(vs);
    } catch { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [book.id]);

  const filtered = useMemo(() => bills.filter((b) => {
    if (filterStatus !== "all" && b.status !== filterStatus) return false;
    if (filterVendor && b.vendor_id !== filterVendor) return false;
    if (q) {
      const lq = q.toLowerCase();
      if (!(b.internal_number || "").toLowerCase().includes(lq) &&
          !(b.bill_number || "").toLowerCase().includes(lq) &&
          !(b.vendor_name || "").toLowerCase().includes(lq)) return false;
    }
    return true;
  }), [bills, filterStatus, filterVendor, q]);

  if (activeBill) {
    return <BillDetail bookId={book.id} billId={activeBill} accounts={accounts} lang={lang} onBack={() => { setActiveBill(null); refresh(); }} onChanged={refresh} />;
  }

  return (
    <div className="space-y-6" data-testid="bills-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1">
          <button onClick={() => setView("bills")} className={`px-4 py-2 text-xs uppercase tracking-widest border ${view === "bills" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`} data-testid="sub-bills">
            <Receipt className="w-3.5 h-3.5 inline mr-1" /> {lang === "es" ? "Facturas" : "Bills"}
          </button>
          <button onClick={() => setView("vendors")} className={`px-4 py-2 text-xs uppercase tracking-widest border ${view === "vendors" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`} data-testid="sub-vendors">
            <Truck className="w-3.5 h-3.5 inline mr-1" /> {lang === "es" ? "Proveedores" : "Vendors"} ({vendors.length})
          </button>
          <button onClick={() => setView("1099")} className={`px-4 py-2 text-xs uppercase tracking-widest border ${view === "1099" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`} data-testid="sub-1099">
            <FileCheck className="w-3.5 h-3.5 inline mr-1" /> 1099-NEC
          </button>
        </div>
        {view === "bills" ? (
          <button onClick={() => { if (!vendors.length) { toast.error("Add a vendor first"); setView("vendors"); return; } setShowNewBill(true); }} className="rn3-btn-primary" data-testid="new-bill-btn"><Plus className="w-4 h-4" /> {lang === "es" ? "Nueva Factura" : "New Bill"}</button>
        ) : view === "vendors" ? (
          <div className="flex items-center gap-2">
            <button onClick={() => setBulkVendorStmts(true)} className="rn3-btn-gold" data-testid="bulk-vendor-stmts-btn"><Send className="w-4 h-4" /> {lang === "es" ? "Envío Masivo" : "Bulk Statements"}</button>
            <button onClick={() => setShowNewVend(true)} className="rn3-btn-primary" data-testid="new-vendor-btn"><Plus className="w-4 h-4" /> {lang === "es" ? "Nuevo Proveedor" : "New Vendor"}</button>
          </div>
        ) : null}
      </div>

      {view === "1099" && <Report1099Panel bookId={book.id} lang={lang} />}

      {view === "bills" && (
        <>
          <APAgingPanel bookId={book.id} lang={lang} onVendorClick={(vid) => { setFilterVendor(vid); setFilterStatus("all"); }} />
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 border border-[#E5E5E5]">
              <Search className="w-3.5 h-3.5 text-[#737373]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "es" ? "Buscar…" : "Search…"} className="bg-transparent outline-none text-sm w-56" />
            </div>
            <select value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)} className="border border-[#E5E5E5] px-3 py-2 text-sm">
              <option value="">{lang === "es" ? "Todos los Proveedores" : "All Vendors"}</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <div className="flex gap-1">
              {[["all", lang==="es"?"Todas":"All"], ...Object.entries(STATUS_PILLS).map(([k, v]) => [k, lang==="es"?v.es:v.en])].map(([k, label]) => (
                <button key={k} onClick={() => setFilterStatus(k)} className={`px-3 py-2 text-xs uppercase tracking-wider border ${filterStatus===k?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"border-[#E5E5E5] hover:border-[#C4A252]"}`}>{label}</button>
              ))}
            </div>
          </div>

          <div className="border border-[#E5E5E5] overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]" data-testid="bills-table">
              <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
                <tr>
                  <th className="py-3 px-3">#</th>
                  <th className="py-3 px-3">{lang === "es" ? "Proveedor" : "Vendor"}</th>
                  <th className="py-3 px-3">{lang === "es" ? "Fecha" : "Date"}</th>
                  <th className="py-3 px-3">{lang === "es" ? "Vence" : "Due"}</th>
                  <th className="py-3 px-3 text-right">{lang === "es" ? "Total" : "Total"}</th>
                  <th className="py-3 px-3 text-right">{lang === "es" ? "Saldo" : "Balance"}</th>
                  <th className="py-3 px-3">{lang === "es" ? "Estado" : "Status"}</th>
                  <th className="py-3 px-3 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="8" className="py-8 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan="8" className="py-12 text-center text-[#737373]">
                    <Receipt className="w-8 h-8 mx-auto mb-2 text-[#C4A252]" />
                    {lang === "es" ? "Sin facturas." : "No bills yet. Add one to get started."}
                  </td></tr>
                )}
                {filtered.map((b) => {
                  const pill = STATUS_PILLS[b.status] || STATUS_PILLS.unpaid;
                  return (
                    <tr key={b.id} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7] cursor-pointer" onClick={() => setActiveBill(b.id)} data-testid={`bill-row-${b.id}`}>
                      <td className="py-2 px-3 font-mono text-[#8B1C22]">{b.internal_number}</td>
                      <td className="py-2 px-3"><div>{b.vendor_name}</div>{b.vendor_company && <div className="text-xs text-[#737373]">{b.vendor_company}</div>}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{b.date}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{b.due_date}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmt(b.total)}</td>
                      <td className={`py-2 px-3 text-right tabular-nums ${b.balance > 0 ? "text-[#8B1C22]" : "text-[#15803D]"}`}>{fmt(b.balance)}</td>
                      <td className="py-2 px-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${pill.cls}`}>{lang === "es" ? pill.es : pill.en}</span></td>
                      <td className="py-2 px-3"><ChevronRight className="w-4 h-4 text-[#737373]" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === "vendors" && (
        <div className="border border-[#E5E5E5]">
          <table className="w-full text-sm" data-testid="vendors-table">
            <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
              <tr>
                <th className="py-3 px-3">{lang === "es" ? "Nombre" : "Name"}</th>
                <th className="py-3 px-3">{lang === "es" ? "Empresa" : "Company"}</th>
                <th className="py-3 px-3">{lang === "es" ? "Contacto" : "Contact"}</th>
                <th className="py-3 px-3">1099</th>
                <th className="py-3 px-3 text-right">{lang === "es" ? "Saldo Abierto" : "Open Balance"}</th>
                <th className="py-3 px-3 text-right">{lang === "es" ? "Pagado YTD" : "YTD Paid"}</th>
                <th className="py-3 px-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 && (
                <tr><td colSpan="7" className="py-12 text-center text-[#737373]">
                  <Truck className="w-8 h-8 mx-auto mb-2 text-[#C4A252]" />
                  {lang === "es" ? "Sin proveedores aún." : "No vendors yet. Add one to start tracking bills."}
                </td></tr>
              )}
              {vendors.map((v) => (
                <tr key={v.id} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7]" data-testid={`vend-row-${v.id}`}>
                  <td className="py-2 px-3 font-medium">{v.name}</td>
                  <td className="py-2 px-3 text-[#737373]">{v.company || ""}</td>
                  <td className="py-2 px-3 text-xs text-[#737373]">{v.email}{v.email && v.phone ? " · " : ""}{v.phone}</td>
                  <td className="py-2 px-3">{v.is_1099 ? <FileCheck className="w-4 h-4 text-[#C4A252]" /> : <span className="text-[#737373]">—</span>}</td>
                  <td className={`py-2 px-3 text-right tabular-nums ${v.open_balance > 0 ? "text-[#8B1C22]" : ""}`}>{fmt(v.open_balance || 0)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmt(v.ytd_paid || 0)}</td>
                  <td className="py-2 px-3 text-right space-x-3">
                    <button onClick={() => setStmtVend(v)} className="text-[#737373] hover:text-[#0A0A0A] inline-flex items-center" data-testid={`vend-stmt-${v.id}`} title={lang === "es" ? "Estado de cuenta" : "Statement"}><FileDown className="w-4 h-4" /></button>
                    <button onClick={() => setEditVend(v)} className="text-[#737373] hover:text-[#0A0A0A]" data-testid={`vend-edit-${v.id}`}><Edit3 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showNewBill} onClose={() => setShowNewBill(false)} title={lang === "es" ? "Nueva Factura" : "New Bill"} testid="new-bill-modal" wide>
        <BillForm bookId={book.id} vendors={vendors} expenseAccounts={expenseAccounts} lang={lang} onCancel={() => setShowNewBill(false)} onSaved={(d) => { setShowNewBill(false); refresh(); setActiveBill(d.id); }} />
      </Modal>
      <Modal open={showNewVend} onClose={() => setShowNewVend(false)} title={lang === "es" ? "Nuevo Proveedor" : "New Vendor"} testid="new-vend-modal">
        <VendorForm bookId={book.id} lang={lang} onCancel={() => setShowNewVend(false)} onSaved={() => { setShowNewVend(false); refresh(); }} />
      </Modal>
      <Modal open={!!editVend} onClose={() => setEditVend(null)} title={lang === "es" ? "Editar Proveedor" : "Edit Vendor"} testid="edit-vend-modal">
        {editVend && <VendorForm bookId={book.id} lang={lang} existing={editVend} onCancel={() => setEditVend(null)} onSaved={() => { setEditVend(null); refresh(); }} />}
      </Modal>
      <StatementModal open={!!stmtVend} onClose={() => setStmtVend(null)} kind="vendor" bookId={book.id} party={stmtVend} lang={lang} />
      <BulkSendModal open={bulkVendorStmts} onClose={() => setBulkVendorStmts(false)} bookId={book.id} kind="vendor" lang={lang} onSent={() => refresh()} />
    </div>
  );
};

export default BillsTab;
